import { useCallback, useEffect, useRef, useState } from "react";
import { tryApiGet } from "../api/client";
import { HoverTip } from "./HoverTip";
import { relativeTime } from "../lib/format";
import { useAppData } from "../state/AppData";
import { sinceQuery, type MessageWindow } from "../lib/messageTimeFilter";
import { observerDisplayNames } from "../lib/auditTable";
import {
  channelLabel, clockWithZone, copiesTip, foundByTip, dateTimeWithZone, hopCountLabel, hopHint, hopLabel, hopTag, mergeNewest, PATH_BADGE, pathBadgeTip,
  FOUND_MARK_TIP, SCOPE_ROW_TIP, scopeCountsTip, observerCountLabel, offsetLabel, publicChannelTip, rateLabel, rateTip, rssiLabel, scopeFilterName, scopeFilterRate, scopeOptions, scopePill, snrLabel, splitPaths,
  visibleUnreadable,
  type ChannelHop, type ChannelList, type ChannelMessage, type ChannelMessageDetail, type ChannelMessagePage,
  type ChannelObserver, type ChannelScope, type ChannelScopeOption, type ChannelScopeTotals, type ChannelSummary,
} from "../lib/channels";

// Read-only view of MeshCore channel messages that this server decrypted
// itself from raw packets. Nothing on this tab can send anything.

const REFRESH_MS = 30_000;
const PAGE = 50;

function enc(s: string): string {
  return encodeURIComponent(s);
}

export function ChannelsTab({ timeWindow }: { timeWindow: MessageWindow }) {
  const [list, setList] = useState<ChannelList | null | "unavailable">(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChannelMessage[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openHash, setOpenHash] = useState<string | null>(null);
  const [showAllUnknown, setShowAllUnknown] = useState(false);
  const [scopeFilter, setScopeFilter] = useState("");
  const [scopeSeen, setScopeSeen] = useState<ChannelScopeOption[] | null>(null);
  const [scopeTotals, setScopeTotals] = useState<ChannelScopeTotals | null>(null);
  const loadingOlder = useRef(false);

  // --- channel list (refreshed in the background) -------------------------
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await tryApiGet<ChannelList>("/api/channels/meshcore");
        if (!cancelled) setList(res ?? "unavailable");
      } catch {
        if (!cancelled) setList((prev) => prev ?? "unavailable");
      }
    }
    void load();
    const t = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, []);

  const channels: ChannelSummary[] = list && list !== "unavailable" ? list.channels : [];
  const retentionDays = list && list !== "unavailable" ? list.retention_days : null;
  const readable = channels.filter((c) => c.readable);
  const unreadableAll = channels.filter((c) => !c.readable);
  const unreadable = visibleUnreadable(unreadableAll, showAllUnknown);
  const selectedChannel = readable.find((c) => c.name === selected) ?? null;
  const filterRate = scopeFilterRate(scopeFilter, scopeSeen, scopeTotals);
  const headerPerHour = scopeFilter && filterRate !== null ? filterRate : selectedChannel?.per_hour ?? 0;
  const scopeFilterLabel = scopeFilter ? scopeFilterName(scopeFilter, scopeSeen) : "";

  useEffect(() => {
    if (selected === null && readable.length > 0) setSelected(readable[0].name);
  }, [selected, readable]);

  // --- messages of the selected channel -----------------------------------
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    // The window's lower bound is recomputed per request so the background refresh keeps a rolling window.
    const query = () => `limit=${PAGE}${scopeFilter ? `&scope=${enc(scopeFilter)}` : ""}${sinceQuery(timeWindow)}`;
    setMessages([]);
    setNextBefore(null);
    setOpenHash(null);
    setError(null);
    setLoading(true);
    tryApiGet<ChannelMessagePage>(`/api/channels/meshcore/${enc(selected)}/messages?${query()}`)
      .then((page) => {
        if (cancelled) return;
        setMessages(page?.messages ?? []);
        setNextBefore(page?.next_before ?? null);
        setScopeSeen(page?.scopes ?? null);
        setScopeTotals(page?.scope_totals ?? null);
      })
      .catch(() => !cancelled && setError("Couldn't load this channel's messages."))
      .finally(() => !cancelled && setLoading(false));
    // Newest page again every so often; older pages already loaded stay put.
    const t = window.setInterval(() => {
      tryApiGet<ChannelMessagePage>(`/api/channels/meshcore/${enc(selected)}/messages?${query()}`)
        .then((page) => {
          if (cancelled || !page) return;
          setMessages((prev) => mergeNewest(prev, page.messages));
          if (page.scopes) setScopeSeen(page.scopes);
          if (page.scope_totals) setScopeTotals(page.scope_totals);
        })
        .catch(() => undefined);
    }, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [selected, scopeFilter, timeWindow]);

  const loadOlder = useCallback(async () => {
    if (!selected || !nextBefore || loadingOlder.current) return;
    loadingOlder.current = true;
    try {
      const page = await tryApiGet<ChannelMessagePage>(
        `/api/channels/meshcore/${enc(selected)}/messages?limit=${PAGE}&before=${enc(nextBefore)}${scopeFilter ? `&scope=${enc(scopeFilter)}` : ""}${sinceQuery(timeWindow)}`,
      );
      if (page) {
        setMessages((prev) => [...prev, ...page.messages]);
        setNextBefore(page.next_before);
      }
    } catch {
      setError("Couldn't load older messages.");
    } finally {
      loadingOlder.current = false;
    }
  }, [selected, nextBefore, scopeFilter, timeWindow]);

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) void loadOlder();
  }

  if (list === null) return <div className="channels-tab"><div className="empty-state">Loading channels…</div></div>;
  if (list === "unavailable") {
    return <div className="channels-tab"><div className="empty-state">Channel messages aren't available here yet.</div></div>;
  }

  return (
    <div className="channels-tab" data-detail={openHash ? "open" : "closed"}>
      <aside className="channels-list">
        {readable.map((c) => (
          <button key={c.name} type="button" className="channels-list-row" data-active={c.name === selected} onClick={() => { if (c.name !== selected) { setScopeFilter(""); setScopeSeen(null); setScopeTotals(null); } setSelected(c.name); }}>
            <ChannelName c={c} />
            <RateText perHour={c.per_hour} />
          </button>
        ))}
        {unreadable.length > 0 && (
          <>
            <div className="channels-list-divider">
              <HoverTip text="These channels use a key this server doesn't have, so their messages can't be read. Only how busy they are is shown.">
                <span>Can't be read</span>
              </HoverTip>
            </div>
            {unreadable.map((c) => (
              <div key={c.channel_hash} className="channels-list-row" data-disabled="true" aria-disabled="true">
                <span className="channels-list-name">{channelLabel(c)}</span>
                <RateText perHour={c.per_hour} />
              </div>
            ))}
            {(showAllUnknown || unreadable.length < unreadableAll.length) && (
              <button type="button" className="channels-list-more" onClick={() => setShowAllUnknown((v) => !v)}>
                {showAllUnknown ? "Show fewer" : `Show all (${unreadableAll.length})`}
              </button>
            )}
          </>
        )}
      </aside>

      <section className="channels-messages">
        <select
          className="channels-select"
          aria-label="Channel"
          value={selected ?? ""}
          onChange={(e) => { setSelected(e.target.value); setScopeFilter(""); setScopeSeen(null); setScopeTotals(null); }}
        >
          {readable.map((c) => (
            <option key={c.name} value={c.name ?? ""}>{channelLabel(c)} · {rateLabel(c.per_hour)}</option>
          ))}
          {unreadable.map((c) => (
            <option key={c.channel_hash} value="" disabled>{channelLabel(c)} · {rateLabel(c.per_hour)}</option>
          ))}
        </select>

        <div className="channels-scroll" onScroll={onScroll}>
          {selectedChannel && (
            <div className="channels-pane-head">
              <ChannelName c={selectedChannel} />
              {scopeFilter && <><span aria-hidden="true">·</span><span>{scopeFilterLabel}</span></>}
              <span aria-hidden="true">·</span>
              <RateText perHour={headerPerHour} />
            </div>
          )}
          <label className="channels-scope-filter">
            <span className="channels-msg-meta">Region scope</span>
            <HoverTip text={scopeCountsTip(retentionDays)}>
              <select
                className="channels-scope-select"
                aria-label="Filter by scope"
                value={scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value)}
              >
                {scopeOptions(scopeSeen, scopeTotals).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </HoverTip>
          </label>
          {error && <div className="auth-error">{error}</div>}
          {loading && messages.length === 0 && <div className="empty-state">Loading messages…</div>}
          {!loading && messages.length === 0 && !error && (
            <div className="empty-state">{scopeFilter ? "No messages with this scope in this time range." : timeWindow === "all" ? "No messages heard on this channel yet." : "No messages in this time range."}</div>
          )}
          {messages.map((m) => (
            <button
              key={m.packet_hash}
              type="button"
              className="channels-msg"
              data-active={m.packet_hash === openHash}
              onClick={() => setOpenHash(m.packet_hash)}
            >
              <span className="channels-msg-head">
                <span className="channels-msg-who">
                  <span className="channels-msg-sender">{m.sender ?? "Unknown sender"}</span>
                  <ScopePillView m={m} />
                </span>
                <HoverTip text={dateTimeWithZone(m.heard_at)}>
                  <span className="channels-msg-time">{relativeTime(m.heard_at)}</span>
                </HoverTip>
              </span>
              <span className="channels-msg-text">{m.text}</span>
              <span className="channels-msg-meta">{observerCountLabel(m.observer_count)}</span>
            </button>
          ))}
          {nextBefore && (
            <button type="button" className="settings-save-btn channels-older" onClick={() => void loadOlder()}>
              Load older messages
            </button>
          )}
        </div>
      </section>

      {openHash && <MessagePanel hash={openHash} onClose={() => setOpenHash(null)} />}
    </div>
  );
}

function ScopePillView({ m }: { m: ChannelScope }) {
  const pill = scopePill(m);
  if (!pill) return null;
  return (
    <HoverTip text={pill.tip}>
      <span className="channels-scope-pill" data-muted={pill.muted} data-region={pill.region}>{pill.label}</span>
    </HoverTip>
  );
}

function ChannelName({ c }: { c: ChannelSummary }) {
  const found = foundByTip(c);
  const tip = [publicChannelTip(c.name), found].filter(Boolean).join(" ") || null;
  const label = (
    <span className="channels-list-name">
      {channelLabel(c)}
      {found && <span className="channels-found-mark">found</span>}
    </span>
  );
  // The bubble opens to the right of the hovered row, level with it.
  return tip ? <HoverTip text={tip} placement="side" anchorTo=".channels-list-row">{label}</HoverTip> : label;
}

function RateText({ perHour }: { perHour: number }) {
  return (
    <HoverTip text={rateTip(perHour)}>
      <span className="channels-list-rate">{rateLabel(perHour)}</span>
    </HoverTip>
  );
}

function PathLine({ hops }: { hops: ChannelHop[] }) {
  if (hops.length === 0) return <span className="channels-hop-none">Direct</span>;
  return (
    <>
      {hops.map((h, j) => {
        const hint = hopHint(h);
        const label = <span className="channels-hop" data-status={h.status}>{hopLabel(h)}</span>;
        return (
          <span key={j}>
            {j > 0 && " → "}
            {hint ? <HoverTip text={hint}>{label}</HoverTip> : label}
            {hopTag(h) && (
              <HoverTip text={pathBadgeTip(h.n_short_id)}>
                <span className="path-tag" aria-label={hopTag(h) ?? undefined}>{PATH_BADGE}</span>
              </HoverTip>
            )}
          </span>
        );
      })}
    </>
  );
}

function MessagePanel({ hash, onClose }: { hash: string; onClose: () => void }) {
  const [detail, setDetail] = useState<ChannelMessageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { observers: appObservers } = useAppData();
  const names = observerDisplayNames(appObservers);
  const observerName = (o: ChannelObserver) =>
    o.is_own || o.source === "own_radio"
      ? names[o.observer_id] ?? "our radio"
      : names[o.observer_id] ?? o.name ?? o.observer_id.slice(0, 8).toUpperCase();

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    tryApiGet<ChannelMessageDetail>(`/api/channels/meshcore/messages/${enc(hash)}`)
      .then((d) => {
        if (cancelled) return;
        if (d) setDetail(d);
        else setError("This message is no longer stored.");
      })
      .catch(() => !cancelled && setError("Couldn't load this message."));
    return () => {
      cancelled = true;
    };
  }, [hash]);

  return (
    <aside className="channels-detail" role="dialog" aria-label="Message details">
      <div className="panel-header">
        <span>Message details</span>
        <button className="close-btn" onClick={onClose} aria-label="Close">✕</button>
      </div>
      {error && <div className="auth-error">{error}</div>}
      {!detail && !error && <div className="empty-state">Loading…</div>}
      {detail && (() => {
        const first = detail.observers.find((o) => o.heard_at)?.heard_at ?? detail.heard_at;
        const { common, differing } = splitPaths(detail.observers);
        const hasRssi = detail.observers.some((o) => o.rssi !== null && o.rssi !== undefined);
        return (
          <div className="channels-detail-body">
            <div className="channels-detail-msg">
              <div className="channels-msg-head">
                <span className="channels-msg-who">
                  <span className="channels-msg-sender">{detail.sender ?? "Unknown sender"}</span>
                  <ScopePillView m={detail} />
                </span>
                <HoverTip text={dateTimeWithZone(detail.heard_at)}>
                  <span className="channels-msg-time">{relativeTime(detail.heard_at)}</span>
                </HoverTip>
              </div>
              <div className="channels-detail-text">{detail.text}</div>
              <div className="channels-msg-meta">{detail.channel_name} · {observerCountLabel(detail.observers.length || detail.observer_count)}</div>
            </div>
            <div className="channels-path-once">
              <HoverTip text={SCOPE_ROW_TIP}>
                <span className="channels-path-label">Region scope:</span>
              </HoverTip>
              {scopePill(detail) ? <ScopePillView m={detail} /> : <span className="channels-msg-meta">--</span>}
            </div>
            {common && (
              <div className="channels-path-once">
                <HoverTip text="The repeaters the message passed through before it was heard, in order. A name means one known node matches that ID, on its own or through the rest of the path (tagged Resolved By Path); otherwise only the short ID is shown.">
                  <span className="channels-path-label">Path:</span>
                </HoverTip>
                <span className="channels-path-hops"><PathLine hops={common.hops} /></span>
                <span className="channels-msg-meta">{hopCountLabel(common.hop_count)}</span>
              </div>
            )}
            <div className="node-list-table-wrap">
              <table className="node-list-table channels-obs-table">
                <thead>
                  <tr>
                    <th>Heard by</th>
                    <th>When</th>
                    <th>
                      <HoverTip text="How clearly this observer received the message, in decibels. Higher is better; below about -10 dB is weak.">
                        <span>SNR</span>
                      </HoverTip>
                    </th>
                    {hasRssi && <th>RSSI</th>}
                  </tr>
                </thead>
                <tbody>
                  {detail.observers.map((o, i) => {
                    const n = o.copy_count ?? o.copies?.length ?? 1;
                    return (
                      <tr key={`${o.observer_id}-${i}`}>
                        <td>
                          <div>{observerName(o)}</div>
                          {differing.has(o) && (
                            <div className="channels-obs-path">
                              <PathLine hops={o.hops} /> <span className="channels-msg-meta">{hopCountLabel(o.hop_count)}</span>
                            </div>
                          )}
                        </td>
                        <td className="channels-nowrap">
                          {o.heard_at ? (
                            <HoverTip text={clockWithZone(o.heard_at)}><span>{offsetLabel(o.heard_at, first)}</span></HoverTip>
                          ) : "--"}
                        </td>
                        <td className="channels-nowrap">
                          {snrLabel(o.snr)}
                          {n > 1 && o.copies && (
                            <>{" "}<HoverTip text={copiesTip(o.copies)}><span className="channels-copies">×{n}</span></HoverTip></>
                          )}
                        </td>
                        {hasRssi && <td className="channels-nowrap">{rssiLabel(o.rssi)}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {detail.observers.length === 0 && <div className="empty-state">No observer details are stored for this message{detail.heard_at ? ` (heard ${relativeTime(detail.heard_at)})` : ""}.</div>}
          </div>
        );
      })()}
    </aside>
  );
}
